import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, isEmpty, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineCheckCircle,
  MdOutlineSubtitles,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  FRANCE_DEPARTMENTS,
  GENDER,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

const Children = ({
  formProps,
  values,
  setFieldValue,
  setPercent,
  isFieldRequired,
  errors,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const COUNTRIES = p("DETAIL.COUNTRIES", { returnObjects: true });

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.CHILD,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN}-section`}
      >
        <Form
          type="vertical"
          title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.TITLE}
          subtitle={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.SUBTITLE}
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert
                showIcon
                type="info"
                message={
                  SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.LMF_INFO
                }
              />
            </Col>
            {get(initialValues, "children", []).map((_, index) => [
              <Col lg={24} xs={24} key={"child-form-" + index}>
                <Form
                  type="highlight"
                  title={
                    SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.CHILD
                      .TITLE +
                    " " +
                    (index + 1)
                  }
                >
                  <Row gutter={[16, 16]}>
                    <Col lg={24} xs={24}>
                      <Input
                        label={SUBSCRIPTION.FORM.LAST_NAME.LABEL}
                        icon={<MdOutlineBadge size={16} />}
                        placeholder={SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER}
                        defaultValue={get(
                          initialValues,
                          `children.${index}.identity.user.lastname`,
                        )}
                        errors={
                          errors[`children[${index}].identity.user.lastname`]
                        }
                        touched={
                          !isEmpty(
                            errors[`children[${index}].identity.user.lastname`],
                          )
                        }
                        onChange={({ target: { value } }) => {
                          set(
                            initialValues,
                            `children.${index}.identity.user.lastname`,
                            value,
                          );
                        }}
                        onBlur={() => setPercent(initialValues)}
                        required={isFieldRequired(
                          `children.${index}.identity.user.lastname`,
                        )}
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Input
                        placeholder={SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER}
                        label={SUBSCRIPTION.FORM.FIRST_NAME.LABEL}
                        icon={<MdOutlineBadge size={16} />}
                        defaultValue={get(
                          initialValues,
                          `children.${index}.identity.user.firstname`,
                        )}
                        errors={
                          errors[`children[${index}].identity.user.firstname`]
                        }
                        touched={
                          !isEmpty(
                            errors[
                              `children[${index}].identity.user.firstname`
                            ],
                          )
                        }
                        onChange={({ target: { value } }) => {
                          set(
                            initialValues,
                            `children.${index}.identity.user.firstname`,
                            value,
                          );
                        }}
                        onBlur={() => setPercent(initialValues)}
                        required={isFieldRequired(
                          `children.${index}.identity.user.firstname`,
                        )}
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Input
                        type="radio"
                        variant="group"
                        label={SUBSCRIPTION.FORM.CHILD_GENDER.LABEL}
                        values={[
                          {
                            label: SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS.MALE,
                            value: GENDER.MALE,
                          },
                          {
                            label:
                              SUBSCRIPTION.FORM.CHILD_GENDER.OPTIONS.FEMALE,
                            value: GENDER.FEMALE,
                          },
                        ]}
                        icon={<MdOutlineBadge size={22} />}
                        value={get(
                          initialValues,
                          `children.${index}.identity.user.gender`,
                        )}
                        errors={
                          errors[`children[${index}].identity.user.gender`]
                        }
                        touched={
                          !isEmpty(
                            errors[`children[${index}].identity.user.gender`],
                          )
                        }
                        onChange={(value) => {
                          set(
                            initialValues,
                            `children.${index}.identity.user.gender`,
                            value,
                          );
                        }}
                        onBlur={() => setPercent(initialValues)}
                        required={isFieldRequired(
                          `children.${index}.identity.user.gender`,
                        )}
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Input
                        label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                        icon={<MdOutlineCake size={16} />}
                        placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                        type="date"
                        variant="simple"
                        defaultValue={dayjs(
                          get(
                            initialValues,
                            `children.${index}.identity.user.birthDate`,
                          ),
                        )}
                        format={DATE_FORMAT}
                        disabled
                        required={isFieldRequired(
                          `children.${index}.identity.user.birthDate`,
                        )}
                      />
                    </Col>

                    <Col lg={24} xs={24}>
                      <Input
                        label={SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL}
                        icon={<MdOutlineSubtitles size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.PLACEHOLDER
                        }
                        trim={true}
                        defaultValue={get(
                          initialValues,
                          `children.${index}.identity.socialSecurityNumber`,
                        )}
                        errors={
                          errors[
                            `children[${index}].identity.socialSecurityNumber`
                          ]
                        }
                        touched={
                          !isEmpty(
                            errors[
                              `children[${index}].identity.socialSecurityNumber`
                            ],
                          )
                        }
                        onChange={({ target: { value } }) => {
                          set(
                            initialValues,
                            `children.${index}.identity.socialSecurityNumber`,
                            value,
                          );
                        }}
                        onBlur={() => setPercent(initialValues)}
                        required={isFieldRequired(
                          `children.${index}.identity.socialSecurityNumber`,
                        )}
                        optional={
                          !isFieldRequired(
                            `children.${index}.identity.socialSecurityNumber`,
                          )
                        }
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Select
                        label={SUBSCRIPTION.FORM.BIRTH_COUNTRY.LABEL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.BIRTH_COUNTRY.PLACEHOLDER
                        }
                        options={COUNTRIES.map((name) => ({
                          label: name,
                          value: name,
                        }))}
                        showSearch
                        allowClear
                        defaultValue={get(
                          initialValues,
                          `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                        )}
                        errors={
                          errors[
                            `children[${index}].identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`
                          ]
                        }
                        touched={
                          !isEmpty(
                            errors[
                              `children[${index}].identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`
                            ],
                          )
                        }
                        onChange={(value) => {
                          set(
                            initialValues,
                            `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                            value,
                          );
                          if (value !== "France") {
                            set(
                              initialValues,
                              `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                              "Autre/Etranger",
                            );
                          }
                          setPercent(initialValues);
                        }}
                        onBlur={() => setPercent(initialValues)}
                        required={isFieldRequired(
                          `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
                        )}
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Input
                        label={SUBSCRIPTION.FORM.BIRTH_CITY.LABEL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={SUBSCRIPTION.FORM.BIRTH_CITY.PLACEHOLDER}
                        defaultValue={get(
                          initialValues,
                          `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                        )}
                        errors={
                          errors[
                            `children[${index}].identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`
                          ]
                        }
                        touched={
                          !isEmpty(
                            errors[
                              `children[${index}].identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`
                            ],
                          )
                        }
                        onChange={({ target: { value } }) => {
                          set(
                            initialValues,
                            `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                            value,
                          );
                        }}
                        onBlur={() => setPercent(initialValues)}
                        required={isFieldRequired(
                          `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
                        )}
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Select
                        label={SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.LABEL}
                        icon={<MdOutlineAddRoad size={16} />}
                        placeholder={
                          SUBSCRIPTION.FORM.BIRTH_DEPARTMENT.PLACEHOLDER
                        }
                        showSearch
                        allowClear
                        options={FRANCE_DEPARTMENTS.map(({ code, name }) => ({
                          label: `${code && `(${code})`} ${name}`,
                          value: name,
                        }))}
                        defaultValue={get(
                          initialValues,
                          `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                        )}
                        value={get(
                          initialValues,
                          `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                        )}
                        errors={
                          errors[
                            `children[${index}].identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`
                          ]
                        }
                        touched={
                          !isEmpty(
                            errors[
                              `children[${index}].identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`
                            ],
                          )
                        }
                        onChange={(value) => {
                          set(
                            initialValues,
                            `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                            value,
                          );
                          setPercent(initialValues);
                        }}
                        onBlur={() => setPercent(initialValues)}
                        required={isFieldRequired(
                          `children.${index}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
                        )}
                      />
                    </Col>
                    <Col lg={24} xs={24}>
                      <Input
                        type="switch"
                        label={SUBSCRIPTION.FORM.TELETRANSMISSION.LABEL}
                        icon={<MdOutlineCheckCircle size={16} />}
                        defaultChecked={
                          !get(
                            initialValues,
                            `children.${index}.identity.fields.teletransmission`,
                          )
                        }
                        errors={
                          errors[
                            `children[${index}].identity.fields.teletransmission`
                          ]
                        }
                        touched={
                          !isEmpty(
                            errors[
                              `children[${index}].identity.fields.teletransmission`
                            ],
                          )
                        }
                        onChange={(value) => {
                          set(
                            initialValues,
                            `children.${index}.identity.fields.teletransmission`,
                            !value,
                          );
                        }}
                      />
                    </Col>
                  </Row>
                </Form>
              </Col>,
            ])}
          </Row>
        </Form>
      </div>
    )
  );
};

export default Children;
